import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Inbox, AlertTriangle, CheckCircle2, RefreshCw,
  PauseCircle, Loader2, ArrowRight,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * Brief 3.4.8 — Admin Onboarding Sessions list view.
 *
 * Super-admin only. Surfaces every session in the
 * `telegram_group_onboarding_sessions` collection — the in-flight
 * Telegram-driven onboarding pipeline. Filterable by status; rows are
 * deeplinks into `/admin/onboarding-sessions/{session_id}`.
 */
const STATUS_GROUPS = [
  { key: "all", label: "All" },
  { key: "pending_intro", label: "Pending intro" },
  { key: "awaiting_answers", label: "Awaiting answers" },
  { key: "awaiting_confirmation", label: "Awaiting confirmation" },
  { key: "duplicate_pending_review", label: "Duplicate review" },
  { key: "awaiting_ops_review", label: "Ops review" },
  { key: "provisioning_pending", label: "Provisioning queued" },
  { key: "done", label: "Done" },
  { key: "marked_duplicate", label: "Marked duplicate" },
  { key: "aborted", label: "Aborted" },
];

const STATUS_PILL = {
  pending_intro: "border-zinc-500/30 bg-zinc-500/[0.08] text-zinc-300",
  awaiting_answers: "border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-100",
  awaiting_confirmation: "border-cyan-400/30 bg-cyan-400/[0.10] text-cyan-100",
  duplicate_pending_review: "border-amber-400/30 bg-amber-400/[0.06] text-amber-100",
  awaiting_ops_review: "border-amber-400/30 bg-amber-400/[0.10] text-amber-100",
  awaiting_handle_clarification: "border-zinc-500/30 bg-zinc-500/[0.08] text-zinc-300",
  provisioning_pending: "border-cyan-400/30 bg-cyan-400/[0.10] text-cyan-100",
  provisioning_in_progress: "border-cyan-400/30 bg-cyan-400/[0.14] text-cyan-100",
  done: "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100",
  marked_duplicate: "border-rose-400/30 bg-rose-400/[0.08] text-rose-100",
  aborted: "border-rose-400/30 bg-rose-400/[0.04] text-rose-200",
};

export default function AdminOnboardingSessions() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/onboarding-sessions", { params });
      setData(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load sessions.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  if (user?.role !== "super_admin") {
    return (
      <div data-testid="onboarding-sessions-forbidden" className="text-rose-200 text-[13px]">
        Super-admin only.
      </div>
    );
  }

  return (
    <div data-testid="onboarding-sessions-page" className="max-w-6xl space-y-6 pb-16">
      <header>
        <div className="flex items-center gap-2.5">
          <Inbox className="h-4 w-4 text-cyan-300/80" />
          <h1 className="text-[18px] tracking-wide text-white">Onboarding sessions</h1>
        </div>
        <p className="mt-2 text-[12.5px] text-zinc-400 max-w-2xl">
          Telegram-driven group onboarding flow. Each session represents
          a prospect who added Nerea to a group. Use the filters to find
          sessions stuck on duplicate review or ops review.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => setFilter(g.key)}
            data-testid={`onboarding-sessions-filter-${g.key}`}
            className={`rounded-full border px-3 py-1 text-[11.5px] transition-colors ${filter === g.key ? "border-white/[0.20] bg-white/[0.08] text-white" : "border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:bg-white/[0.04]"}`}
          >
            {g.label}
          </button>
        ))}
        <button
          type="button"
          onClick={refresh}
          data-testid="onboarding-sessions-refresh"
          className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-zinc-400 hover:text-white transition-colors"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="onboarding-sessions-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-4 text-rose-200 text-[12.5px]">
          {error}
        </div>
      )}

      {!error && data && data.sessions.length === 0 && (
        <div data-testid="onboarding-sessions-empty" className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-6 text-[12.5px] text-zinc-500">
          No sessions in this state.
        </div>
      )}

      {data && data.sessions.length > 0 && (
        <ul className="space-y-2">
          {data.sessions.map((s) => (
            <SessionRow key={s.session_id} session={s} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SessionRow({ session }) {
  const pill = STATUS_PILL[session.status] || "border-white/[0.08] text-zinc-300";
  const legal = session.extracted_state?.legal_name?.value;
  const dupCount = (session.duplicate_candidates || []).length;
  return (
    <li data-testid={`onboarding-session-row-${session.session_id}`} className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
      <Link
        to={`/admin/onboarding-sessions/${session.session_id}`}
        className="block"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center rounded-full border ${pill} px-2 py-0.5 text-[10.5px] tracking-wide`}>
                {session.status}
              </span>
              <span className="text-[12.5px] text-zinc-100 truncate">
                {legal || <span className="text-zinc-500 italic">(legal name not yet captured)</span>}
              </span>
            </div>
            <div className="mt-1 text-[11px] text-zinc-500 font-mono">
              chat_id={session.chat_id} · {session.session_id}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {dupCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-2 py-0.5 text-[10.5px] text-amber-200">
                <AlertTriangle className="h-3 w-3" /> {dupCount} dup
              </span>
            )}
            <ArrowRight className="h-3.5 w-3.5 text-zinc-500" />
          </div>
        </div>
      </Link>
    </li>
  );
}
